import { useState } from "react";
import PropTypes from "prop-types";
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import { Link, withRouter } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { withTranslation } from "react-i18next";
import dayjs from "dayjs";

import BreadCrumb from "../../Components/Common/BreadCrumb";
import StockStatusBadge from "./components/StockStatusBadge";
import { formatPrice } from "../../constants/promotedProducts";
import {
  useInvalidatePromotedProduct,
  usePendingProducts,
  useValidatePromotedProduct,
} from "../../hooks/usePromotedProducts";
import { normalizeBackendFileUrl } from "../../utils/backendApi";
import { resolveApiErrorMessage } from "../../utils/resolveApiErrorMessage";
import defaultIllustration from "../../assets/images/lcbtp.png";

const PendingProducts = (props) => {
  const [page, setPage] = useState(1);
  const [confirmValidate, setConfirmValidate] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const pendingQ = usePendingProducts({ page, pageSize: 20 });
  const validateMutation = useValidatePromotedProduct();
  const invalidateMutation = useInvalidatePromotedProduct();

  const payload = pendingQ.data;
  const products = payload?.results ?? [];
  const count = payload?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(count / 20));

  const handleValidate = () => {
    if (!confirmValidate?.id) return;
    validateMutation.mutate(confirmValidate.id, {
      onSettled: () => setConfirmValidate(null),
    });
  };

  const handleReject = () => {
    if (!rejectTarget?.id) return;
    invalidateMutation.mutate(
      { id: rejectTarget.id, rejectionReason: rejectReason },
      {
        onSettled: () => {
          setRejectTarget(null);
          setRejectReason("");
        },
      }
    );
  };

  document.title = props.t("Produits en attente de validation");

  return (
    <div className="page-content">
      <ToastContainer closeButton={false} />

      {/* Validation confirmation — spells out the consequence. */}
      <Modal isOpen={Boolean(confirmValidate)} toggle={() => setConfirmValidate(null)} centered>
        <ModalHeader toggle={() => setConfirmValidate(null)}>
          Valider ce produit ?
        </ModalHeader>
        <ModalBody>
          <p className="mb-0">
            <b>{confirmValidate?.designation}</b> sera{" "}
            <b>immédiatement visible</b> sur l&apos;application mobile.
          </p>
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="btn btn-light"
            onClick={() => setConfirmValidate(null)}
          >
            Annuler
          </button>
          <button
            type="button"
            className="btn btn-success"
            onClick={handleValidate}
            disabled={validateMutation.isPending}
          >
            {validateMutation.isPending ? "Validation…" : "Valider"}
          </button>
        </ModalFooter>
      </Modal>

      {/* Rejection modal — asks for a reason. */}
      <Modal isOpen={Boolean(rejectTarget)} toggle={() => setRejectTarget(null)} centered>
        <ModalHeader toggle={() => setRejectTarget(null)}>
          Rejeter « {rejectTarget?.designation} »
        </ModalHeader>
        <ModalBody>
          <Label htmlFor="rejectReason">Motif du rejet</Label>
          <Input
            id="rejectReason"
            type="textarea"
            rows={3}
            placeholder="Ex. Photos de mauvaise qualité, prix non renseigné."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
          <small className="text-muted">
            Le motif est conservé et affiché sur le détail du produit.
          </small>
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="btn btn-light"
            onClick={() => setRejectTarget(null)}
          >
            Annuler
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleReject}
            disabled={invalidateMutation.isPending}
          >
            {invalidateMutation.isPending ? "Rejet…" : "Rejeter"}
          </button>
        </ModalFooter>
      </Modal>

      <Container fluid>
        <BreadCrumb
          title={props.t("En attente de validation")}
          pageTitle={props.t("Promotion de produits")}
        />

        <Row className="mb-3">
          <Col>
            <Link to="/promoted-products" className="btn btn-light">
              <i className="ri-arrow-left-line align-bottom me-1" />
              Tous les produits
            </Link>
          </Col>
        </Row>

        {pendingQ.isLoading ? (
          <Row>
            {[...Array(3)].map((_, i) => (
              <Col lg={6} key={i}>
                <Card>
                  <CardBody>
                    <div className="placeholder-glow" style={{ height: 120 }}>
                      <span className="placeholder col-12 h-100" />
                    </div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        ) : pendingQ.isError ? (
          <Card>
            <CardBody className="text-center py-5">
              <h5>
                {resolveApiErrorMessage(pendingQ.error, "Une erreur est survenue")}
              </h5>
              <button
                type="button"
                className="btn btn-primary mt-2"
                onClick={() => pendingQ.refetch()}
              >
                Réessayer
              </button>
            </CardBody>
          </Card>
        ) : products.length === 0 ? (
          <Card>
            <CardBody className="text-center py-5">
              <i className="ri-checkbox-circle-line display-5 text-success" />
              <h5 className="mt-3">Aucun produit en attente de validation</h5>
              <p className="text-muted mb-0">
                Tous les produits enregistrés ont été traités.
              </p>
            </CardBody>
          </Card>
        ) : (
          <>
            <Row>
              {products.map((product) => (
                <Col lg={6} key={product.id}>
                  <Card>
                    <CardBody>
                      <div className="d-flex gap-3">
                        <div className="d-flex flex-column gap-1">
                          <img
                            src={
                              normalizeBackendFileUrl(product.cover_image) ||
                              defaultIllustration
                            }
                            alt={product.designation}
                            className="rounded"
                            style={{
                              width: 96,
                              height: 96,
                              objectFit: "cover",
                            }}
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = defaultIllustration;
                            }}
                          />
                          <small className="text-muted text-center">
                            {product.images?.length || 0} image
                            {(product.images?.length || 0) > 1 ? "s" : ""}
                          </small>
                        </div>

                        <div className="flex-grow-1">
                          <h5 className="fs-15 mb-1">{product.designation}</h5>
                          <p className="mb-1 fw-semibold text-primary">
                            {formatPrice(product.price, product.currency)}
                            {product.unit ? ` / ${product.unit}` : ""}
                            <span className="text-muted fw-normal">
                              {" "}
                              • {product.stock_quantity} en stock
                            </span>
                          </p>
                          <p className="mb-1">
                            <StockStatusBadge
                              status={product.stock_status}
                              label={product.stock_status_label}
                            />
                            {product.category?.designation && (
                              <span className="badge badge-soft-secondary ms-1">
                                {product.category.designation}
                              </span>
                            )}
                          </p>
                          <small className="text-muted">
                            Créé par {product.created_by?.full_name || "—"} —{" "}
                            {dayjs(product.created_at).format("DD/MM/YYYY")}
                          </small>

                          {product.rejection_reason ? (
                            <div className="alert alert-warning py-1 px-2 mt-2 mb-0 fs-12">
                              <b>Motif précédent :</b> {product.rejection_reason}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      <div className="d-flex gap-2 mt-3">
                        <button
                          type="button"
                          className="btn btn-success btn-sm"
                          onClick={() => setConfirmValidate(product)}
                          disabled={(product.images?.length || 0) === 0}
                          title={
                            (product.images?.length || 0) === 0
                              ? "Ajoutez au moins une image pour pouvoir valider"
                              : undefined
                          }
                        >
                          <i className="ri-check-line align-bottom me-1" />
                          Valider
                        </button>
                        <button
                          type="button"
                          className="btn btn-soft-danger btn-sm"
                          onClick={() => {
                            setRejectTarget(product);
                            setRejectReason(product.rejection_reason || "");
                          }}
                        >
                          <i className="ri-close-line align-bottom me-1" />
                          Rejeter
                        </button>
                        <Link
                          to={`/promoted-products-details/${product.id}`}
                          className="btn btn-light btn-sm"
                        >
                          Voir
                        </Link>
                        {(product.images?.length || 0) === 0 && (
                          <Link
                            to={`/promoted-products-update/${product.id}`}
                            className="btn btn-soft-warning btn-sm"
                          >
                            Ajouter une image
                          </Link>
                        )}
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              ))}
            </Row>

            {totalPages > 1 && (
              <div className="d-flex justify-content-end align-items-center gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  ‹
                </button>
                <span className="text-muted">
                  Page {page} / {totalPages}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  ›
                </button>
              </div>
            )}
          </>
        )}
      </Container>
    </div>
  );
};

PendingProducts.propTypes = {
  t: PropTypes.any,
};

export default withRouter(withTranslation()(PendingProducts));
